import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  ListItemText,
  Checkbox,
  OutlinedInput,
  Divider,
  Alert
} from "@mui/material";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const HRUpdateRoles = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const isEditMode = state?.isEdit;
  const [errors, setErrors] = useState({});

  // ✅ Define default role types first
  const defaultRoleTypes = ["CEO", "CTO", "Board of directors", "Section manager/Department manger", "Manager", "Project Manager", "Product Manager", "Product Owner", "Finance Manager", "HR", "HR Manager", "Hiring Manager", "Priciple engineer/architect", "Team Lead", "Senior Software Engineer", "Software Engineer", "Junior Software Engineer", "Intern"];

  const responsibilities = {
    "CEO": ["Strategic planning", "Business development", "Executive leadership", "Stakeholder relations", "Company vision"],
    "CTO": ["Technology strategy", "Technical leadership", "Innovation management", "IT governance", "Technical risk assessment"],
    "Board of directors": ["Corporate governance", "Strategic oversight", "CEO supervision", "Financial accountability", "Shareholder representation"],
    "Section manager/Department manger": ["Departmental leadership", "Budget management", "Cross-team coordination", "Department KPI tracking", "Process improvement"],
    "Manager": ["Team management", "Project oversight", "Performance evaluation", "Resource allocation", "Department strategy"],
    "HR Manager": ["Employee relations", "HR policy development", "Benefits administration", "Employee engagement", "Talent development"],
    "Hiring Manager": ["Candidate screening", "Interview coordination", "Recruitment strategy", "Onboarding oversight", "Hiring decisions"],
    "Priciple engineer/architect": ["System architecture design", "Technical decision-making", "Engineering standards", "Technical mentorship", "Technology roadmap"],
    "Team Lead": ["Team coordination", "Technical guidance", "Sprint planning", "Progress reporting", "Code reviews"],
    "Senior Software Engineer": ["Complex feature implementation", "Architecture participation", "Technical mentoring", "Advanced problem solving", "Code quality enforcement"],
    "Software Engineer": ["Software development", "Problem solving", "Code testing", "Documentation", "Feature implementation"],
    "Junior Software Engineer": ["Basic feature implementation", "Bug fixing", "Learning codebase", "Pair programming", "Testing and QA"],
    "Intern": ["Learning", "Assigned tasks", "Progress reporting", "Skill development", "Support activities"]
  };

  const [formData, setFormData] = useState({
    employeeDetails: {
      employeeId: state?.employee_id || "",
      fullName: "",
      email: "",
      designation: "",
      joiningDate: "",
      department: "",
      promotionDate: "",
      grade: ""
    },
    roleInfo: {
      roleType: "",
      reportingManager: "",
      teamSize: "",
      accessLevel: "",
      shiftType: "",
      loginTime: "",
      logoutTime: "",
    },
    responsibilities: {
      selectedResponsibilities: [],
      additionalResponsibilities: "",
    }
  });

  const [originalDesignation, setOriginalDesignation] = useState("");
  const [originalPromotionDate, setOriginalPromotionDate] = useState("");
  const [loading, setLoading] = useState(false);
  
  // ✅ State for role type handling
  const [roleTypes, setRoleTypes] = useState(defaultRoleTypes);
  const [isCustomRole, setIsCustomRole] = useState(false);
  const [customRoleInput, setCustomRoleInput] = useState("");
  const [showResponsibilities, setShowResponsibilities] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.employeeDetails.employeeId.trim()) {
      newErrors.employeeId = "Employee ID is required";
    }

    if (!formData.employeeDetails.joiningDate.trim()) {
      newErrors.joiningDate = "Joining Date is required";
    }

    if (!formData.roleInfo.roleType.trim()) {
      newErrors.roleType = "Role Type is required";
    }

    if (formData.employeeDetails.promotionDate && formData.employeeDetails.promotionDate.trim()) {
      if (!formData.employeeDetails.designation.trim()) {
        newErrors.designation = "New designation is required for promotion";
      } else if (formData.employeeDetails.designation.trim().toLowerCase() === originalDesignation.trim().toLowerCase()) {
        newErrors.designation = "New designation must be different for promotion";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // ✅ Fetch existing roles from database
  const fetchExistingRoles = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      
      const response = await axios.get(`${API_URL}/api/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const roles = response.data.data.map(r => r.roleType).filter(Boolean);
        const uniqueRoles = [...new Set(roles)];
        const allRoles = [...new Set([...defaultRoleTypes, ...uniqueRoles])];
        setRoleTypes(allRoles);
      }
    } catch (error) {
      //console.error("Error fetching roles:", error);
    }
  };

  useEffect(() => {
    fetchExistingRoles(); // ✅ Call this
    const fetchEmployeeData = async () => {
      if (state?.employee_id) {
        try {
          const token = localStorage.getItem("token");
          if (!token) {
            alert("No authentication token found. Please log in.");
            return;
          }

          const payload = JSON.parse(atob(token.split('.')[1]));
          const userId = payload.id;

          setLoading(true);

          const employeeResponse = await axios.get(
            `${API_URL}/api/employees/${state.employee_id}?id=${userId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              }
            }
          );
          if (employeeResponse.data.success && employeeResponse.data.data) {
            const employeeData = employeeResponse.data.data;

            setFormData(prev => ({
              ...prev,
              employeeDetails: {
                ...prev.employeeDetails,
                employeeId: state.employee_id,
                fullName: `${employeeData.firstName} ${employeeData.lastName}`,
                email: employeeData.companyemail,
                department: employeeData.department || ""
              }
            }));
          }

          if (isEditMode) {
            const rolesResponse = await axios.get(
              `${API_URL}/api/roles/${state.employee_id}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                }
              }
            );
            
            if (rolesResponse.data.success && rolesResponse.data.data) {
              const rolesData = rolesResponse.data.data;

              setOriginalDesignation(rolesData.designation || "");
              setOriginalPromotionDate(formatDateForInput(rolesData.promotionDate) || "");

              const roleTypeFromAPI = rolesData.roleType || "";
              
              // ✅ Check if role is predefined or custom
              const isPredefinedRole = defaultRoleTypes.some(r => 
                r.toLowerCase() === roleTypeFromAPI.toLowerCase()
              );

              let formattedRoleType = "";
              if (roleTypeFromAPI) {
                const foundRole = defaultRoleTypes.find(role =>
                  role.toLowerCase() === roleTypeFromAPI.toLowerCase()
                );
                formattedRoleType = foundRole || roleTypeFromAPI;
              }

              // ✅ Set custom role state if needed
              if (!isPredefinedRole && roleTypeFromAPI) {
                setIsCustomRole(true);
                setCustomRoleInput(roleTypeFromAPI);
                setShowResponsibilities(false);
              } else {
                setIsCustomRole(false);
                setCustomRoleInput("");
                const hasResp = responsibilities[formattedRoleType] !== undefined;
                setShowResponsibilities(hasResp);
              }

              setFormData(prev => ({
                ...prev,
                employeeDetails: {
                  ...prev.employeeDetails,
                  designation: rolesData.designation || "",
                  joiningDate: formatDateForInput(rolesData.joiningDate),
                  department: rolesData.department || prev.employeeDetails.department,
                  promotionDate: "",
                  grade: rolesData.grade || ""
                },
                roleInfo: {
                  roleType: isPredefinedRole ? formattedRoleType : roleTypeFromAPI,
                  reportingManager: rolesData.reportingManager || "",
                  teamSize: rolesData.teamSize || "",
                  accessLevel: rolesData.accessLevel || "",
                  shiftType: rolesData.shiftType || "",
                  loginTime: rolesData.loginTime || "",
                  logoutTime: rolesData.logoutTime || "",
                },
                responsibilities: {
                  selectedResponsibilities: rolesData.selectedResponsibilities || [],
                  additionalResponsibilities: rolesData.additionalResponsibilities || "",
                }
              }));
            }
          }
        } catch (error) {
          //console.error("Error fetching data:", error);
          alert("Failed to load employee and role details");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchEmployeeData();
  }, [state?.employee_id, isEditMode]);

  // ✅ Keep this for all fields except roleType
  const handleChange = (e, section, field) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: e.target.value
      }
    }));

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  // ✅ Handle role type changes
  const handleRoleTypeChange = (e) => {
    const value = e.target.value;
    
    const isPredefined = defaultRoleTypes.some(r => 
      r.toLowerCase() === value.toLowerCase()
    );
    
    if (!isPredefined && value !== "" && value !== "Other") {
      // This is a custom role
      setIsCustomRole(true);
      setCustomRoleInput(value);
      setShowResponsibilities(false);
      setFormData(prev => ({
        ...prev,
        roleInfo: { ...prev.roleInfo, roleType: value }
      }));
    } else if (value === "" || value === "Other") {
      // Empty selection or "Other" selected
      setIsCustomRole(value === "Other");
      setCustomRoleInput("");
      setShowResponsibilities(false);
      setFormData(prev => ({
        ...prev,
        roleInfo: { ...prev.roleInfo, roleType: value === "Other" ? "" : value }
      }));
    } else {
      // Predefined role
      setIsCustomRole(false);
      setCustomRoleInput("");
      const hasResponsibilities = responsibilities[value] !== undefined;
      setShowResponsibilities(hasResponsibilities);
      setFormData(prev => ({
        ...prev,
        roleInfo: { ...prev.roleInfo, roleType: value }
      }));
    }
  };

  // ✅ Handle custom role input
  const handleCustomRoleChange = (e) => {
    const value = e.target.value;
    setCustomRoleInput(value);
    setFormData(prev => ({
      ...prev,
      roleInfo: { ...prev.roleInfo, roleType: value }
    }));
    setShowResponsibilities(false);
  };

  const handleResponsibilitiesChange = (event) => {
    const { target: { value } } = event;
    setFormData(prev => ({
      ...prev,
      responsibilities: {
        ...prev.responsibilities,
        selectedResponsibilities: typeof value === 'string' ? value.split(',') : value,
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }

    const payloadToken = JSON.parse(atob(token.split('.')[1]));
    const userId = payloadToken.id;

    try {
      const isPromotion = formData.employeeDetails.promotionDate &&
        formData.employeeDetails.promotionDate.trim() !== "";

      const payload = {
        employee_id: formData.employeeDetails.employeeId,
        fullName: formData.employeeDetails.fullName,
        email: formData.employeeDetails.email,
        designation: formData.employeeDetails.designation,
        joiningDate: formData.employeeDetails.joiningDate,
        department: formData.employeeDetails.department,
        roleType: formData.roleInfo.roleType,
        reportingManager: formData.roleInfo.reportingManager,
        teamSize: parseInt(formData.roleInfo.teamSize) || 0,
        accessLevel: formData.roleInfo.accessLevel,
        shiftType: formData.roleInfo.shiftType,
        loginTime: formData.roleInfo.loginTime,
        logoutTime: formData.roleInfo.logoutTime,
        selectedResponsibilities: formData.responsibilities.selectedResponsibilities,
        additionalResponsibilities: formData.responsibilities.additionalResponsibilities,
        isPromotion: isPromotion,
        promotionDate: isPromotion ? formData.employeeDetails.promotionDate : null,
        grade: formData.employeeDetails.grade
      };

      const response = await axios({
        method: isEditMode ? "put" : "post",
        url: isEditMode
          ? `${API_URL}/api/roles/${formData.employeeDetails.employeeId}`
          : `${API_URL}/api/roles&id=${userId}`,
        data: payload,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success || response.status === 201) {
        const message = response.data.data?.isPromotion
          ? `🎉 ${formData.employeeDetails.fullName} promoted successfully!\n\nPrevious Role Duration:\n${response.data.data.previousDuration.years} years, ${response.data.data.previousDuration.months} months, ${response.data.data.previousDuration.days} days\n\nPromotion Date: ${response.data.data.promotionDate}`
          : `✅ ${isEditMode ? 'Role updated' : 'Role added'} successfully!`;

        alert(message);
        navigate("/viewrole");
      }
    } catch (error) {
      //console.error("Submission error:", error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to save roles and responsibilities details";
      alert(`Error: ${errorMessage}`);
    }
  };

  const isPromotionActive = formData.employeeDetails.promotionDate &&
    formData.employeeDetails.promotionDate.trim() !== "";

  return (
    <Box sx={{ maxWidth: 1500, margin: "auto", padding: 3 }}>
      <Typography variant="h4" gutterBottom align="center" fontWeight="bold">
        {isEditMode ? "Edit Roles & Responsibilities" : "Add Roles & Responsibilities"}
      </Typography>

      {isPromotionActive && (
        <Alert severity="info" sx={{ mb: 3 }}>
          🎉 Promotion Mode Active: Old role will be archived to history when you submit
        </Alert>
      )}

      <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
        <Typography variant="h6" gutterBottom>Employee Details</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="Employee ID"
              fullWidth
              value={formData.employeeDetails.employeeId}
              onChange={(e) => handleChange(e, "employeeDetails", "employeeId")}
              required
              error={!!errors.employeeId}
              helperText={errors.employeeId}
              disabled={location.state?.employee_id || isEditMode}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Full Name"
              fullWidth
              value={formData.employeeDetails.fullName}
              onChange={(e) => handleChange(e, "employeeDetails", "fullName")}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Email"
              fullWidth
              value={formData.employeeDetails.email}
              onChange={(e) => handleChange(e, "employeeDetails", "email")}
              disabled={location.state?.employee_id || isEditMode}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Designation"
              fullWidth
              value={formData.employeeDetails.designation}
              onChange={(e) => handleChange(e, "employeeDetails", "designation")}
              error={!!errors.designation}
              helperText={errors.designation || (originalDesignation ? `Current: ${originalDesignation}` : "")}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Joining Date"
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              value={formData.employeeDetails.joiningDate}
              onChange={(e) => handleChange(e, "employeeDetails", "joiningDate")}
              required
              error={!!errors.joiningDate}
              helperText={errors.joiningDate}
              disabled={isEditMode}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Department"
              fullWidth
              value={formData.employeeDetails.department}
              onChange={(e) => handleChange(e, "employeeDetails", "department")}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Grade"
              fullWidth
              value={formData.employeeDetails.grade}
              onChange={(e) => handleChange(e, "employeeDetails", "grade")}
            />
          </Grid>

          {isEditMode && (
            <Grid item xs={12}>
              <TextField
                label="Promotion Date (Fill only if promoting)"
                fullWidth
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.employeeDetails.promotionDate}
                onChange={(e) => handleChange(e, "employeeDetails", "promotionDate")}
                helperText={
                  originalPromotionDate
                    ? `Last Promotion: ${originalPromotionDate}. Fill this field to promote employee to new designation.`
                    : "Fill this field to promote employee. This will archive current role to history."
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: isPromotionActive ? '#ff9800' : undefined,
                      borderWidth: isPromotionActive ? 2 : 1
                    }
                  }
                }}
              />
            </Grid>
          )}
        </Grid>
      </Paper>

      <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
        <Typography variant="h6" gutterBottom>Role Information</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControl fullWidth error={!!errors.roleType}>
              <InputLabel id="role-type-label">Role Type *</InputLabel>
              <Select
                labelId="role-type-label"
                id="role-type-select"
                value={isCustomRole ? "Other" : formData.roleInfo.roleType}
                label="Role Type"
                onChange={handleRoleTypeChange}
                required
              >
                {roleTypes.map((role) => (
                  <MenuItem key={role} value={role}>{role}</MenuItem>
                ))}
                <MenuItem value="Other">+ Other (Custom)</MenuItem>
              </Select>
              {errors.roleType && (
                <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                  {errors.roleType}
                </Typography>
              )}
            </FormControl>
            
            {isCustomRole && (
              <TextField
                label="Enter Custom Role Type"
                fullWidth
                value={customRoleInput}
                onChange={handleCustomRoleChange}
                placeholder="e.g., Senior Architect, Lead Developer"
                sx={{ mt: 2 }}
                required
                error={!!errors.roleType}
                helperText={errors.roleType || "Enter a custom role type"}
              />
            )}
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Reporting Manager Email"
              fullWidth
              value={formData.roleInfo.reportingManager}
              onChange={(e) => handleChange(e, "roleInfo", "reportingManager")}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Team Size"
              fullWidth
              type="number"
              value={formData.roleInfo.teamSize}
              onChange={(e) => handleChange(e, "roleInfo", "teamSize")}
            />
          </Grid>
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>Shift Details</Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <FormControl fullWidth>
              <InputLabel>Shift Type</InputLabel>
              <Select
                value={formData.roleInfo.shiftType}
                label="Shift Type"
                onChange={(e) => handleChange(e, "roleInfo", "shiftType")}
              >
                <MenuItem value="General Shift">General Shift</MenuItem>
                <MenuItem value="Early Shift">Early Shift</MenuItem>
                <MenuItem value="Evening Shift">Evening Shift</MenuItem>
                <MenuItem value="Night Shift">Night Shift</MenuItem>
                <MenuItem value="Hybrid Shift">Hybrid Shift</MenuItem>
                <MenuItem value="Remote Shift">Remote Shift</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              type="time"
              label="Login Time"
              value={formData.roleInfo.loginTime}
              onChange={(e) => handleChange(e, "roleInfo", "loginTime")}
              InputLabelProps={{ shrink: true }}
              helperText="The total duration between login and logout must be 24 hours"
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              type="time"
              label="Logout Time"
              value={formData.roleInfo.logoutTime}
              onChange={(e) => handleChange(e, "roleInfo", "logoutTime")}
              InputLabelProps={{ shrink: true }}
              helperText="The total duration between login and logout must be 24 hours"
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
        <Typography variant="h6" gutterBottom>Responsibilities</Typography>
        <Grid container spacing={2}>
          {showResponsibilities && formData.roleInfo.roleType && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="responsibilities-label">Select Responsibilities</InputLabel>
                <Select
                  labelId="responsibilities-label"
                  id="responsibilities-select"
                  multiple
                  value={formData.responsibilities.selectedResponsibilities}
                  onChange={handleResponsibilitiesChange}
                  input={<OutlinedInput label="Select Responsibilities" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} />
                      ))}
                    </Box>
                  )}
                >
                  {formData.roleInfo.roleType && responsibilities[formData.roleInfo.roleType] ?
                    responsibilities[formData.roleInfo.roleType].map((resp) => (
                      <MenuItem key={resp} value={resp}>
                        <Checkbox checked={formData.responsibilities.selectedResponsibilities.indexOf(resp) > -1} />
                        <ListItemText primary={resp} />
                      </MenuItem>
                    )) :
                    <MenuItem disabled>No predefined responsibilities for this role</MenuItem>
                  }
                </Select>
              </FormControl>
            </Grid>
          )}
          
          <Grid item xs={12}>
            <TextField
              label="Additional Responsibilities"
              fullWidth
              multiline
              rows={4}
              value={formData.responsibilities.additionalResponsibilities}
              onChange={(e) => handleChange(e, "responsibilities", "additionalResponsibilities")}
              placeholder="Describe any additional responsibilities not covered above..."
            />
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={6}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => navigate("/viewrole")}
          >
            Cancel
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{ marginLeft: 45 }}
          >
            {isPromotionActive ? "Promote Employee" : (isEditMode ? 'Update' : 'Submit')}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HRUpdateRoles;